import React, { useState, useEffect } from "react";
import { Drawer, List, Checkbox, Input, message, Button, Spin, AutoComplete } from "antd";
import { getInvoiceItemAno, updateInvoiceItem, updateInvoice } from "../../../actions/invoiceItem";
import QuickviewCommon from "../QuickViewCommon";

const PrintInvoiceItem = ({ visible, onClose, invoice, onReload }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [isPrintingComplete, setIsPrintingComplete] = useState(false);

  useEffect(() => {
    if (visible && invoice) {
      setLoading(true);
      getInvoiceItemAno(invoice.id)(() => { }).then(res => {
        setItems((res && res.results) ? res.results : (res || []));
        setLoading(false);
      });
    } else {
      setItems([]);
      setBarcode("");
      setCountdown(0);
      setIsPrintingComplete(false);
    }
  }, [visible, invoice]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && isPrintingComplete) {
      setIsPrintingComplete(false);
      onClose();
      if (onReload) onReload();
      message.success("Invoice Printed successfully!");
    }
  }, [countdown, isPrintingComplete, onClose, onReload]);

  const handlePrintComplete = async () => {
    if (!invoice) return;
    setCountdown(40);
    setIsPrintingComplete(true);
    try {
      await updateInvoice(invoice.id, { is_printable: false, status: "Ready" })(() => { });
      message.success("Print complete! Invoice is now not printable and status changed to Ready.");
    } catch (e) {
      message.error("Failed to update invoice.");
      setCountdown(0);
    }
  };

  const handleCheckItem = async (item, newQty, isChecked) => {
    const newItems = items.map(i => i.id === item.id ? { ...i, is_checked: isChecked, checked_quantity: newQty } : i);
    setItems(newItems);
    await updateInvoiceItem(item.id, { is_checked: isChecked, checked_quantity: newQty })(() => { });
  };

  const handleBarcodeScan = (value) => {
    if (!value || countdown > 0) return;

    // Check if value matches any product barcode
    const matchedItem = items.find(item => {
      const itemBarcode = item.Product && item.Product[0] && item.Product[0].barcode;
      return itemBarcode && String(itemBarcode) === String(value);
    });

    if (matchedItem) {
      const currentQty = matchedItem.checked_quantity || 0;
      if (currentQty < matchedItem.quantity) {
        const newQty = currentQty + 1;
        const isFullyChecked = newQty === matchedItem.quantity;
        handleCheckItem(matchedItem, newQty, isFullyChecked);
        message.success(`Item matched! (${newQty}/${matchedItem.quantity})`);
      } else {
        message.info("All quantities for this item are already checked.");
      }
    } else {
      message.error("Wrong barcode. Item not found in this invoice.");
    }
    setBarcode("");
  };

  const handleSelect = (value, option) => {
    const matchedItem = items.find(i => i.id === option.key);
    if (matchedItem) {
      const currentQty = matchedItem.checked_quantity || 0;
      if (currentQty < matchedItem.quantity) {
        const newQty = currentQty + 1;
        const isFullyChecked = newQty === matchedItem.quantity;
        handleCheckItem(matchedItem, newQty, isFullyChecked);
        message.success(`Item matched! (${newQty}/${matchedItem.quantity})`);
      } else {
        message.info("All quantities for this item are already checked.");
      }
    }
    setBarcode("");
  };

  const getOptions = () => {
    if (!barcode) return [];
    const lowerSearch = barcode.toLowerCase();

    // Search only by barcode
    const matchingItems = items.filter(item => {
      const p = item.Product && item.Product[0];
      if (!p || !p.barcode) return false;
      return String(p.barcode).toLowerCase().includes(lowerSearch);
    });

    return matchingItems.map(item => {
      const p = item.Product[0];
      return {
        value: String(p.barcode),
        key: item.id,
        label: String(p.barcode)
      };
    });
  };

  const allChecked = items.length > 0 && items.every(item => item.is_checked || (item.checked_quantity === item.quantity));

  return (
    <>
      <style>{`
        .colorful-disabled-checkbox .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
          border-color: #fff !important;
        }
        .colorful-disabled-checkbox .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner {
          background-color: #1890ff !important;
          border-color: #1890ff !important;
        }
        .colorful-disabled-checkbox .ant-checkbox-disabled + span {
          color: inherit !important;
          cursor: not-allowed;
        }
      `}</style>
      {countdown > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 99999,
            cursor: "not-allowed",
            background: "transparent",
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
      <Drawer
        title={`Invoice Items for ${(invoice && invoice.invoice_number) ? invoice.invoice_number : ''}`}
        placement="right"
        onClose={onClose}
        visible={visible}
        width={800}
        maskClosable={countdown === 0}
        closable={countdown === 0}
      >
      <Spin spinning={countdown > 0} tip={`Processing... Please wait ${countdown} seconds`}>
        <AutoComplete
          options={getOptions()}
          style={{ width: '100%', marginBottom: 16 }}
          value={barcode}
          onChange={setBarcode}
          onSelect={handleSelect}
          disabled={countdown > 0}
        >
          <Input.Search
            placeholder="Scan or type Barcode..."
            onSearch={value => {
              handleBarcodeScan(value);
            }}
            enterButton
            autoFocus
          />
        </AutoComplete>
        <List
          loading={loading}
          itemLayout="horizontal"
          dataSource={items}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={item.Details || (item.Product && item.Product[0] && item.Product[0].title) || "Unknown Item"}
                description={`Quantity: ${item.quantity} | Price: ${item.price} | Barcode: ${(item.Product && item.Product[0] && item.Product[0].barcode) || "N/A"}`}
              />
              <Checkbox
                className="colorful-disabled-checkbox"
                checked={item.is_checked || (item.checked_quantity === item.quantity)}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  const newQty = isChecked ? item.quantity : 0;
                  handleCheckItem(item, newQty, isChecked);
                }}
                // disabled={countdown > 0}
                disabled={true}
              >
                {(item.checked_quantity || 0)}/{item.quantity} Checked
              </Checkbox>
            </List.Item>
          )}
        />
        {invoice && (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "20px" }}>
            <div style={{ flex: 1 }}>
              <QuickviewCommon
                details={invoice}
                buttonType="button"
                buttonText="Print Complete"
                onAfterPrint={handlePrintComplete}
                disabled={!allChecked || countdown > 0}
              />
            </div>
            <div style={{ borderTop: "1px solid #eee", paddingTop: "20px" }}>
              <QuickviewCommon
                details={invoice}
                inline={true}
              />
            </div>
          </div>
        )}
      </Spin>
    </Drawer>
    </>
  );
};

export default PrintInvoiceItem;
